import emptyBoxCombinedUrl from '@/assets/user-apps/empty-box-combined.svg'
import emptyBoxShapeUrl from '@/assets/user-apps/empty-box-shape.svg'
import emptyOvalUrl from '@/assets/user-apps/empty-oval.png'
import pinFilledIconUrl from '@/assets/user-apps/pin-filled.svg'
import pinOutlineIconUrl from '@/assets/user-apps/pin-outline.svg'
import { Badge } from '@/components/data-display/badge'
import { useTranslation } from 'react-i18next'
import { ErrorState } from '@/components/feedback/error-state'
import { Skeleton } from '@/components/feedback/skeleton'
import { SearchInput } from '@/components/forms/search-input'
import { Modal } from '@/components/overlays/modal'
import { BottomSheet } from '@/components/mobile/bottom-sheet'
import { MobilePageHeader } from '@/components/mobile/page-header'
import { Button } from '@/components/primitives/button'
import { UserShellPage } from '@/features/user/shell/shell-page'
import { cn } from '@/lib/cn'

import type { UserApp } from '../api'
import { appErrorCopy, type AppTab, useAppsPage } from '../use-apps-page'

function AppAvatar({ app, size = 'sm' }: { app: UserApp; size?: 'lg' | 'sm' }) {
  const initial = Array.from(app.name.trim())[0]?.toUpperCase() ?? '?'
  const className = cn(
    'shrink-0 bg-[#e2e9ff] object-cover',
    size === 'lg' ? 'size-16 rounded-[6px]' : 'size-11 rounded-[10px]',
  )
  const fallbackClassName = cn(
    'inline-flex shrink-0 items-center justify-center bg-[#e2e9ff] font-semibold text-[var(--color-primary)]',
    size === 'lg' ? 'size-16 rounded-[6px] text-xl' : 'size-11 rounded-[10px] text-[17px]',
  )

  return app.avatar ? (
    <img alt="" className={className} src={app.avatar} />
  ) : (
    <span className={fallbackClassName}>{initial}</span>
  )
}

function AppsMobileSkeleton() {
  return (
    <div className="user-app-grid-enter grid grid-cols-1 gap-2.5">
      {Array.from({ length: 8 }).map((_, index) => (
        <Skeleton className="app-list-skeleton-item h-[190px] rounded-[14px]" key={index} />
      ))}
    </div>
  )
}

function EmptyAppsState({
  actionLabel,
  message,
  onAction,
}: {
  actionLabel?: string
  message: string
  onAction?: () => void
}) {
  return (
    <div className="flex min-h-[420px] flex-col items-center justify-center text-center">
      <div className="relative h-10 w-16" aria-hidden>
        <img alt="" className="absolute top-[26px] left-0 h-3.5 w-16" src={emptyOvalUrl} />
        <img
          alt=""
          className="absolute top-0 left-[9px] h-[23px] w-[46px]"
          src={emptyBoxShapeUrl}
        />
        <img
          alt=""
          className="absolute top-[13px] left-[9px] h-[23px] w-[46px]"
          src={emptyBoxCombinedUrl}
        />
      </div>
      <p className="mt-5 text-sm leading-5 text-[var(--color-text)]">{message}</p>
      {actionLabel && onAction ? (
        <button
          className="mt-4 h-[38px] cursor-pointer rounded-lg bg-[var(--color-primary)] px-4 text-sm text-white hover:bg-[var(--color-primary-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
          onClick={onAction}
          type="button"
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  )
}

function AppsMobileCard({
  app,
  activeTab,
  busy,
  installPending,
  onInstall,
  onOpenDetails,
  onRequestRemove,
  onToggleFavorite,
  onUseApp,
}: {
  app: UserApp
  activeTab: AppTab
  busy: boolean
  installPending: boolean
  onInstall: (app: UserApp) => void
  onOpenDetails: (app: UserApp) => void
  onRequestRemove: (app: UserApp) => void
  onToggleFavorite: (app: UserApp) => void
  onUseApp: (app: UserApp) => void
}) {
  const { t } = useTranslation('userApps')
  const tags = (app.tags ?? []).slice(0, 2)
  const showFavorite = activeTab === 'mine'
  const showRemove = activeTab === 'mine' && app.is_installed
  const versionLabel = app.version ? `v${app.version.replace(/^v/i, '')}` : t('emptyVersion')

  return (
    <article className="user-app-card group relative flex flex-col rounded-[14px] border border-[var(--color-panel-border-subtle)] bg-white p-3.5 shadow-[var(--shadow-panel-subtle)]">
      {showFavorite ? (
        <button
          aria-label={
            app.is_favorite
              ? t('actions.unfavorite', { name: app.name })
              : t('actions.favorite', { name: app.name })
          }
          className={cn(
            'absolute top-2.5 right-2.5 z-10 inline-flex size-8 cursor-pointer items-center justify-center rounded-md transition-opacity duration-150 hover:bg-[rgba(26,28,25,0.05)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
            app.is_favorite ? 'opacity-100' : 'opacity-35',
          )}
          disabled={busy}
          onClick={() => {
            onToggleFavorite(app)
          }}
          type="button"
        >
          <img
            alt=""
            aria-hidden
            className="size-3.5"
            src={app.is_favorite ? pinFilledIconUrl : pinOutlineIconUrl}
          />
        </button>
      ) : null}
      <div className="grid gap-3">
        <button
          className="grid cursor-pointer grid-cols-[44px_minmax(0,1fr)] gap-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-focus)]"
          onClick={() => {
            if (activeTab === 'mine') {
              onUseApp(app)
              return
            }
            onOpenDetails(app)
          }}
          type="button"
        >
          <AppAvatar app={app} />
          <span className="grid min-w-0 content-start gap-1">
            <span className="flex min-w-0 items-center gap-2 pr-7">
              <span className="min-w-0 truncate text-[15px] leading-[22px] font-semibold text-[var(--color-text-strong)]">
                {app.name}
              </span>
              <span className="shrink-0 text-[11px] leading-4 text-[#8c8e90]">{versionLabel}</span>
            </span>
            <span className="line-clamp-2 overflow-hidden text-[13px] leading-[19px] text-[var(--color-text-muted)]">
              {app.description || t('emptyDescription')}
            </span>
            {tags.length > 0 ? (
              <span className="mt-0.5 flex flex-wrap gap-1">
                {tags.map((tag) => (
                  <Badge className="h-[18px] rounded px-1.5 text-[10px] font-normal" key={tag.id}>
                    {tag.name}
                  </Badge>
                ))}
                {(app.tags?.length ?? 0) > tags.length ? (
                  <Badge className="h-[18px] rounded px-1.5 text-[10px] font-normal">
                    {t('moreTags')}
                  </Badge>
                ) : null}
              </span>
            ) : null}
          </span>
        </button>
        <div className="grid grid-cols-2 gap-2">
          <Button
            aria-label={t('actions.details', { name: app.name })}
            className="h-9 w-full rounded-md px-2 text-sm font-medium"
            onClick={() => {
              onOpenDetails(app)
            }}
            size="sm"
            variant="soft"
          >
            {t('actions.detailsShort')}
          </Button>
          {showRemove ? (
            <Button
              aria-label={t('actions.removeNamed', { name: app.name })}
              className="h-9 w-full rounded-md px-2 text-sm font-medium"
              disabled={busy}
              onClick={() => {
                onRequestRemove(app)
              }}
              size="sm"
              variant="dangerSoft"
            >
              {t('actions.removeShort')}
            </Button>
          ) : !app.is_installed ? (
            <Button
              aria-label={t('actions.installNamed', { name: app.name })}
              className="h-9 w-full rounded-md px-2 text-sm font-medium"
              isLoading={installPending}
              onClick={() => {
                onInstall(app)
              }}
              size="sm"
            >
              {t('actions.installShort')}
            </Button>
          ) : (
            <span className="flex h-9 items-center justify-center gap-1 rounded-md bg-[var(--color-surface-muted)] text-sm font-medium text-[var(--color-text-muted)]">
              <svg aria-hidden className="size-3.5" fill="none" viewBox="0 0 16 16">
                <path
                  d="m3.5 8.4 3 3 6-6.6"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.6"
                />
              </svg>
              {t('actions.installed')}
            </span>
          )}
        </div>
      </div>
    </article>
  )
}

export function AppsMobilePage() {
  const { i18n } = useTranslation('userApps')
  const state = useAppsPage()
  const {
    activeQuery,
    activeTab,
    apps,
    categoryItems,
    favoriteMutation,
    handleUseApp,
    hasActiveFilter,
    installMutation,
    isBusy,
    isInitialLoading,
    operationError,
    pendingRemoveApp,
    requestRemoveApp,
    searchInput,
    selectedApp,
    setActiveTab,
    setPendingRemoveApp,
    setSearchInput,
    setSelectedApp,
    setSelectedTagId,
    showGridSkeleton,
    t,
    uninstallMutation,
    validatedTagId,
  } = state

  return (
    <UserShellPage activePath="/apps">
      <div className="flex h-full flex-col overflow-hidden">
        <MobilePageHeader title={i18n.t('shell:userNavigation.appMarket')} />
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pt-3.5 pb-5">
          <div className="grid shrink-0 gap-3.5">
            <div
              className="flex h-9 items-center gap-1.5 rounded-[10px] bg-[var(--color-surface-muted)] p-[3px]"
              onKeyDown={(event) => {
                if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
                const tabs = Array.from(
                  event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]'),
                )
                const currentIndex = tabs.indexOf(document.activeElement as HTMLElement)
                if (currentIndex < 0) return
                event.preventDefault()
                const nextIndex =
                  event.key === 'ArrowRight'
                    ? (currentIndex + 1) % tabs.length
                    : (currentIndex - 1 + tabs.length) % tabs.length
                const nextTab = tabs[nextIndex]
                nextTab.focus()
                setActiveTab(nextTab.dataset.value as AppTab)
              }}
              role="tablist"
            >
              {(['mine', 'market'] as AppTab[]).map((tab) => (
                <button
                  aria-selected={activeTab === tab}
                  className={cn(
                    'h-[30px] flex-1 cursor-pointer rounded-lg text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                    activeTab === tab
                      ? 'bg-white text-[var(--color-primary)] shadow-[0_1px_2px_rgba(16,24,40,0.08)]'
                      : 'bg-transparent text-[var(--color-text-muted)]',
                  )}
                  data-value={tab}
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab)
                  }}
                  role="tab"
                  tabIndex={activeTab === tab ? 0 : -1}
                  type="button"
                >
                  {t(`tabs.${tab}`)}
                </button>
              ))}
            </div>
            <SearchInput
              aria-label={t('search.label')}
              className="h-10 w-full rounded-lg border-[var(--color-control-border)] shadow-[var(--shadow-control)]"
              onChange={(event) => {
                setSearchInput(event.target.value)
              }}
              placeholder={t('search.placeholderMobile')}
              value={searchInput}
            />
            <div className="flex max-w-full gap-1.5 overflow-x-auto pb-1">
              {categoryItems.map((item) => (
                <button
                  className={cn(
                    'h-8 shrink-0 cursor-pointer rounded-full border px-3.5 text-[13px] font-medium whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                    validatedTagId === item.id
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                      : 'border-[var(--color-panel-border-subtle)] bg-white text-[var(--color-text-muted)]',
                  )}
                  key={item.id || 'all'}
                  onClick={() => {
                    setSelectedTagId(item.id)
                  }}
                  type="button"
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>

          {operationError ? (
            <div className="mt-4 shrink-0">
              <ErrorState
                description={appErrorCopy(
                  operationError,
                  t('errors.operationDescription'),
                  t('errors.unauthorized'),
                  t('errors.forbidden'),
                  t('errors.channelUnavailable'),
                )}
                title={t('errors.operationTitle')}
              />
            </div>
          ) : null}

          <div className="mt-4 min-h-0 flex-1 overflow-auto pb-4">
            {isInitialLoading ? (
              <>
                <div className="app-list-progress" aria-hidden />
                <div className="sr-only" role="status">
                  {t('loading')}
                </div>
              </>
            ) : null}
            {showGridSkeleton ? <AppsMobileSkeleton /> : null}
            {activeQuery.isError && !activeQuery.data ? (
              <ErrorState
                description={appErrorCopy(
                  activeQuery.error,
                  t('errors.listDescription'),
                  t('errors.unauthorized'),
                  t('errors.forbidden'),
                  t('errors.channelUnavailable'),
                )}
                onRetry={() => {
                  void activeQuery.refetch()
                }}
                title={t('errors.listTitle')}
              />
            ) : null}
            {!isInitialLoading && !activeQuery.isError && apps.length === 0 ? (
              hasActiveFilter ? (
                <EmptyAppsState message={t('empty.filtered')} />
              ) : activeTab === 'mine' ? (
                <EmptyAppsState
                  actionLabel={t('empty.goMarket')}
                  message={t('empty.mine')}
                  onAction={() => {
                    setSearchInput('')
                    setSelectedTagId('')
                    setActiveTab('market')
                  }}
                />
              ) : (
                <EmptyAppsState message={t('empty.market')} />
              )
            ) : null}
            {apps.length > 0 ? (
              <div className="user-app-grid-enter grid grid-cols-1 gap-2.5">
                {apps.map((app) => (
                  <AppsMobileCard
                    activeTab={activeTab}
                    app={app}
                    busy={isBusy}
                    installPending={installMutation.isPending}
                    key={app.id}
                    onInstall={(nextApp) => {
                      installMutation.mutate(nextApp.id)
                    }}
                    onOpenDetails={setSelectedApp}
                    onRequestRemove={requestRemoveApp}
                    onToggleFavorite={(nextApp) => {
                      favoriteMutation.mutate(nextApp)
                    }}
                    onUseApp={(nextApp) => {
                      void handleUseApp(nextApp)
                    }}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <BottomSheet
          onClose={() => {
            setSelectedApp(null)
          }}
          open={selectedApp !== null}
          title={selectedApp?.name ?? ''}
          titleVisuallyHidden
        >
          {selectedApp ? (
            <div className="flex max-h-[calc(100dvh-108px)] flex-col">
              <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-1">
                <div className="space-y-4 pt-1">
                  <div className="flex flex-col items-center gap-2 text-center">
                    <AppAvatar app={selectedApp} size="lg" />
                    <p className="text-base leading-6 font-bold text-[var(--color-text)]">
                      {selectedApp.name}
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-1">
                      {(selectedApp.tags ?? []).slice(0, 3).map((tag) => (
                        <Badge
                          className="h-[18px] rounded px-1.5 text-[10px] font-normal"
                          key={tag.id}
                        >
                          {tag.name}
                        </Badge>
                      ))}
                      {selectedApp.version ? (
                        <>
                          {(selectedApp.tags?.length ?? 0) > 0 ? (
                            <span className="mx-1 h-[18px] w-px bg-[#d8d8d8]" aria-hidden />
                          ) : null}
                          <span className="text-xs leading-[18px] text-[#8c8e90]">
                            v{selectedApp.version.replace(/^v/i, '')}
                          </span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <p className="text-xs leading-[18px] text-[#8c8e90]">
                    {selectedApp.description || t('emptyDescription')}
                  </p>
                  {selectedApp.usage_instructions ? (
                    <div>
                      <p className="text-xs leading-[18px] text-[var(--color-text)]">
                        {t('detail.instructions')}
                      </p>
                      <p className="mt-2 whitespace-pre-line text-sm leading-6 text-[var(--color-text)]">
                        {selectedApp.usage_instructions}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="grid shrink-0 grid-cols-2 gap-2 px-5 pt-3 pb-[calc(16px+env(safe-area-inset-bottom))]">
                {selectedApp.is_installed ? (
                  <>
                    <Button
                      className="h-11 flex-1"
                      onClick={() => {
                        void handleUseApp(selectedApp)
                      }}
                    >
                      {t('actions.use')}
                    </Button>
                    <Button
                      className="h-11 flex-1"
                      onClick={() => {
                        requestRemoveApp(selectedApp)
                      }}
                      variant="dangerSoft"
                    >
                      {t('actions.remove')}
                    </Button>
                  </>
                ) : (
                  <Button
                    className="col-span-2 h-11 w-full"
                    isLoading={installMutation.isPending}
                    onClick={() => {
                      installMutation.mutate(selectedApp.id)
                    }}
                  >
                    {t('actions.install')}
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </BottomSheet>
        <Modal
          className="max-w-[400px]"
          closeLabel={t('removeConfirm.close')}
          footer={
            <>
              <Button
                className="flex-1"
                onClick={() => {
                  setPendingRemoveApp(null)
                }}
                variant="secondary"
              >
                {t('removeConfirm.cancel')}
              </Button>
              <Button
                className="flex-1"
                isLoading={uninstallMutation.isPending}
                onClick={() => {
                  if (pendingRemoveApp) uninstallMutation.mutate(pendingRemoveApp.id)
                }}
                variant="danger"
              >
                {t('removeConfirm.confirm')}
              </Button>
            </>
          }
          onClose={() => {
            setPendingRemoveApp(null)
          }}
          open={pendingRemoveApp !== null}
          title={t('removeConfirm.title')}
        >
          <p className="text-sm leading-6 text-[var(--color-text-muted)]">
            {t('removeConfirm.description', { name: pendingRemoveApp?.name ?? '' })}
          </p>
        </Modal>
      </div>
    </UserShellPage>
  )
}
